import type { Tone, TreeView } from '../types';
import { CANVAS, GEO, TONES, toneColor, toneFill } from '../theme';

import { Caption, EmptyCanvas, FONT, LAYOUT, fmt, frameProps, labelWidth, nodeRadiusFor } from './shared';

const R = GEO.nodeRadius;
const PAD = GEO.padding;

interface Placed {
  id: string;
  value: number | string;
  col: number;
  depth: number;
}

/**
 * Tidy binary layout: depth comes from the walk, and x comes from an in-order
 * column counter. A `null` child still consumes a column, which is what makes a
 * right-only child lean right instead of sitting directly under its parent.
 */
function layout(view: TreeView) {
  const map = new Map(view.nodes.map((node) => [node.id, node]));
  const placed: Placed[] = [];
  const links: Array<{ from: string; to: string }> = [];
  const visited = new Set<string>();
  let col = 0;

  const slot = (childId: string | null | undefined, parentId: string, depth: number) => {
    if (childId == null || !map.has(childId) || visited.has(childId)) {
      col += 1; // reserve the empty/duplicate slot so siblings stay on their side
      return;
    }
    links.push({ from: parentId, to: childId });
    walk(childId, depth);
  };

  function walk(id: string, depth: number) {
    if (visited.has(id)) return;
    visited.add(id);
    const node = map.get(id);
    if (!node) return;
    const children = node.children ?? [];
    const mid = Math.ceil(children.length / 2);
    for (let k = 0; k < mid; k += 1) slot(children[k], id, depth + 1);
    placed.push({ id, value: node.value, col: col++, depth });
    for (let k = mid; k < children.length; k += 1) slot(children[k], id, depth + 1);
  }

  if (map.has(view.rootId)) walk(view.rootId, 0);
  return { placed, links };
}

export function TreeRenderer({ view }: { view: TreeView }) {
  const { placed, links } = layout(view);
  const title =
    view.caption ?? (placed.length === 0 ? 'Empty tree' : `Tree of ${placed.length} nodes`);

  if (placed.length === 0) return <EmptyCanvas title={title} />;

  const marks = new Map<string, { tone: Tone; label?: string }>();
  for (const mark of view.marks ?? []) marks.set(mark.nodeId, { tone: mark.tone, label: mark.label });
  const hasLabels = [...marks.values()].some((m) => !!m.label);

  const byId = new Map(placed.map((node) => [node.id, node]));
  const maxCol = Math.max(...placed.map((n) => n.col));
  const maxDepth = Math.max(...placed.map((n) => n.depth));

  /*
   * A node's own value is drawn centred inside its circle — a long one (a
   * model name, a full phrase) would otherwise spill past a fixed-radius
   * circle into the node beside it. Grow every node uniformly to the widest
   * value present, the same way GraphRenderer does; short DSA-style values
   * never exceed the base radius, so this is a no-op for existing content.
   */
  const NR = Math.max(R, ...placed.map((n) => nodeRadiusFor(fmt(n.value), R)));
  const BASE_COL_GAP = NR * 2 + 20;

  /*
   * Labels are drawn to the right of a node, so they need room in two places:
   * past the rightmost column, and — the part that actually bit — between
   * adjacent columns. The default gap leaves only 14px of clear space, so any
   * label longer than a character or two was drawn over the neighbouring node.
   * Widening the column pitch keeps labels legible without the author having to
   * abbreviate to an invisible limit.
   */
  const longestLabel = hasLabels
    ? Math.ceil(Math.max(...[...marks.values()].map((m) => labelWidth(m.label))))
    : 0;
  const labelRoom = hasLabels ? Math.max(48, longestLabel + 12) : 0;
  const COL_GAP = hasLabels ? Math.max(BASE_COL_GAP, NR * 2 + 12 + longestLabel + 8) : BASE_COL_GAP;

  /*
   * A parent sits directly above a child with no horizontal offset when a
   * node has a single child — the fixed base level gap (tuned for a 24px
   * radius) is shorter than an enlarged node's diameter, so they'd overlap
   * vertically even though COL_GAP already keeps same-depth nodes clear.
   */
  const levelGap = Math.max(GEO.levelGap, 2 * NR + 20);

  const captionY = view.caption ? PAD + 8 : 0;
  const top = (view.caption ? PAD + LAYOUT.captionBand : PAD) + NR;
  const x = (col: number) => PAD + NR + col * COL_GAP;
  const y = (depth: number) => top + depth * levelGap;

  const width = PAD * 2 + 2 * NR + maxCol * COL_GAP + labelRoom;
  const height = y(maxDepth) + NR + PAD;

  return (
    <svg {...frameProps(width, height, view.caption)}>
      <title>{title}</title>
      {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}

      <g aria-hidden="true">
        {links.map((link, k) => {
          const from = byId.get(link.from);
          const to = byId.get(link.to);
          if (!from || !to) return null;
          return (
            <line
              key={`edge-${k}`}
              x1={x(from.col)}
              y1={y(from.depth) + NR}
              x2={x(to.col)}
              y2={y(to.depth) - NR}
              stroke={CANVAS.cellBorder}
              strokeWidth={1.5}
            />
          );
        })}
      </g>

      {placed.map((node) => {
        const mark = marks.get(node.id);
        const isCursor = view.cursor === node.id;
        return (
          <g key={node.id}>
            <circle
              cx={x(node.col)}
              cy={y(node.depth)}
              r={NR}
              fill={mark ? toneFill(mark.tone) : CANVAS.cell}
              stroke={isCursor ? TONES.accent : mark ? toneColor(mark.tone) : CANVAS.cellBorder}
              strokeWidth={isCursor ? 3 : 1.5}
            />
            <text
              x={x(node.col)}
              y={y(node.depth)}
              fill={CANVAS.text}
              fontSize={FONT.value}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {fmt(node.value)}
            </text>
            {mark?.label ? (
              <text
                x={x(node.col) + NR + 6}
                y={y(node.depth) - NR + 4}
                fill={toneColor(mark.tone)}
                fontSize={FONT.label}
                textAnchor="start"
                dominantBaseline="central"
              >
                {mark.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

export default TreeRenderer;
